// Native Studio / Studio Workspace B1 — the program compositor + on-air graphics.
// Renders every publisher's camera/screen track host-first (host top-left), then
// the bold-broadcast overlay layer (frame, wordmark logo bug, LIVE bug,
// active-speaker lower-third, ticker). Single source of truth: ViewerStage (watch),
// StudioStage (publish), and later Egress (bare). The ingress `program` is just
// another tile.
//
// Host identity match: every participant's identity is `${userId}-<hex>`
// (makeIdentity), so the host is matched by prefix on the raw hostId — NOT by a
// freshly-computed makeIdentity(hostId), which would carry a different suffix.

import {
  GridLayout,
  ParticipantTile,
  useEnsureTrackRef,
  useIsSpeaking,
  useTracks,
} from '@livekit/components-react';
import { Track } from 'livekit-client';
import { useEffect, useMemo, useRef, useState } from 'react';

import { useFeaturedSuperchatContext } from './useFeaturedSuperchat';
import { livenessState } from './viewerAccess';

import { Logo } from '@/components/brand/Logo';
import { cn } from '@/lib/utils/cn';

type StageTrack = ReturnType<typeof useTracks>[number];

function isHostIdentity(identity: string, hostId: string): boolean {
  return identity === hostId || identity.startsWith(`${hostId}-`);
}

export function BroadcastView({
  roomStatus,
  hostId,
  title,
  tickerLines,
  logoUrl,
}: {
  roomStatus: string;
  hostId: string;
  title: string;
  tickerLines: string[];
  logoUrl?: string | null;
}) {
  const tracks = useTracks([Track.Source.Camera, Track.Source.ScreenShare], {
    onlySubscribed: false,
  }).filter((t) => t.publication?.kind === Track.Kind.Video);

  // Host top-left: stable sort host-first; cohosts keep order in the next cells.
  const ordered = useMemo(() => {
    return [...tracks].sort((a, b) => {
      const aHost = isHostIdentity(a.participant.identity, hostId) ? 0 : 1;
      const bHost = isHostIdentity(b.participant.identity, hostId) ? 0 : 1;
      return aHost - bHost;
    });
  }, [tracks, hostId]);

  const screenShares = ordered.filter((t) => t.source === Track.Source.ScreenShare);
  const cameras = ordered.filter((t) => t.source === Track.Source.Camera);
  const sharing = screenShares.length > 0;

  const liveness = livenessState(tracks.length > 0, roomStatus);

  const overlay = (
    <OverlayLayer
      roomStatus={roomStatus}
      title={title}
      tickerLines={tickerLines}
      logoUrl={logoUrl}
    />
  );

  return (
    <section data-broadcast-view className="relative h-full w-full bg-black">
      {liveness !== 'playing' ? (
        <div className="flex h-full w-full items-center justify-center">
          <p className="font-sans text-sm text-video-text-muted">
            {liveness === 'ended' ? 'This stream has ended.' : 'Reconnecting…'}
          </p>
        </div>
      ) : sharing ? (
        // Presentation mode: shared content fills the frame; cameras stack in a
        // left 1/8 column. Full-bleed (no 16:9 letterboxing) so the shared
        // screen is as large and readable as possible.
        <div className="absolute inset-0">
          <div className="flex h-full w-full">
            {cameras.length > 0 && (
              <div className="flex h-full w-[12.5%] flex-col bg-black">
                {cameras.map((t) => (
                  <div key={t.participant.identity} className="relative min-h-0 flex-1">
                    <BroadcastTile trackRef={t} compact />
                  </div>
                ))}
              </div>
            )}
            <div className="relative h-full flex-1">
              <GridLayout tracks={screenShares} className="h-full w-full">
                <BroadcastTile screen />
              </GridLayout>
            </div>
          </div>
          {overlay}
        </div>
      ) : (
        // Program mode: a single centered, contained 16:9 composition.
        <div className="absolute inset-0 m-auto aspect-video max-h-full max-w-full">
          <GridLayout tracks={ordered} className="h-full w-full">
            <BroadcastTile />
          </GridLayout>
          {overlay}
        </div>
      )}
    </section>
  );
}

// Each grid tile owns its nameplate: the default ParticipantTile (video +
// behavior) with a per-window serif nameplate overlaid bottom-left. The active
// speaker's plate brightens; others sit dimmed. Reflows with the grid.
function BroadcastTile({
  trackRef: tr,
  screen = false,
  compact = false,
}: {
  /** Explicit track (left-column cameras); falls back to GridLayout context. */
  trackRef?: StageTrack;
  /** Screen-share content: object-contain (no crop) and no nameplate. */
  screen?: boolean;
  /** Narrow left-column camera: smaller nameplate. */
  compact?: boolean;
} = {}) {
  const trackRef = useEnsureTrackRef(tr);
  const participant = trackRef.participant;
  const speaking = useIsSpeaking(participant);
  const wrapperRef = useRef<HTMLDivElement>(null);
  // Never crop a person: cameras are contained (letterboxed), and the nameplate
  // tracks the rendered video rect rather than the cell, so it sits on the
  // stream and not on a matte bar.
  const { left, bottomInset } = useContainedVideoRect(wrapperRef);
  return (
    <div
      ref={wrapperRef}
      // bg-video-bg makes the contain matte (letterbox/pillarbox) read as a
      // deliberate frame; the overlay's top accent + inset ring tie it together.
      className="relative h-full w-full bg-video-bg [&_video]:object-contain"
      data-screen-tile={screen || undefined}
    >
      <ParticipantTile trackRef={trackRef} disableSpeakingIndicator />
      {!screen && (
        <Nameplate
          name={participant.name || participant.identity}
          active={speaking}
          compact={compact}
          left={left}
          bottomInset={bottomInset}
        />
      )}
    </div>
  );
}

// Track the rendered video rectangle inside a tile so an overlay can anchor to
// the actual stream (object-contain leaves matte bars when camera aspect ≠ cell
// aspect). Returns the bottom-left offset of the video within the cell.
function useContainedVideoRect(wrapperRef: { current: HTMLDivElement | null }) {
  const [rect, setRect] = useState<{ left: number; bottomInset: number }>({
    left: 0,
    bottomInset: 0,
  });

  useEffect(() => {
    const wrapper = wrapperRef.current;
    if (!wrapper) return;

    let raf = 0;
    let boundVideo: HTMLVideoElement | null = null;

    const compute = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const video = wrapper.querySelector('video');
        const cw = wrapper.clientWidth;
        const ch = wrapper.clientHeight;
        const vW = video?.videoWidth ?? 0;
        const vH = video?.videoHeight ?? 0;
        // First-paint fallback (before metadata): cell bottom-left, no flash.
        if (!video || vW === 0 || vH === 0 || cw === 0 || ch === 0) {
          setRect({ left: 0, bottomInset: 0 });
          return;
        }
        const videoAspect = vW / vH;
        const cellAspect = cw / ch;
        let offX: number;
        let offY: number;
        if (videoAspect >= cellAspect) {
          // Fills width — letterbox bars top/bottom.
          offX = 0;
          offY = (ch - cw / videoAspect) / 2;
        } else {
          // Fills height — pillarbox bars left/right.
          offX = (cw - ch * videoAspect) / 2;
          offY = 0;
        }
        setRect({ left: offX, bottomInset: offY });
      });
    };

    // (Re)bind metadata/resize to the current <video>, which ParticipantTile may
    // mount late or replace on a track swap.
    const bind = () => {
      const video = wrapper.querySelector('video');
      if (video === boundVideo) return;
      boundVideo?.removeEventListener('loadedmetadata', compute);
      boundVideo?.removeEventListener('resize', compute);
      boundVideo = video;
      boundVideo?.addEventListener('loadedmetadata', compute);
      boundVideo?.addEventListener('resize', compute);
      compute();
    };

    bind();
    const ro = new ResizeObserver(compute);
    ro.observe(wrapper);
    const mo = new MutationObserver(bind);
    mo.observe(wrapper, { childList: true, subtree: true });

    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      mo.disconnect();
      boundVideo?.removeEventListener('loadedmetadata', compute);
      boundVideo?.removeEventListener('resize', compute);
    };
  }, [wrapperRef]);

  return rect;
}

function Nameplate({
  name,
  active,
  compact = false,
  left,
  bottomInset,
}: {
  name: string;
  active: boolean;
  compact?: boolean;
  /** Bottom-left offset of the rendered video within the cell (px). */
  left: number;
  bottomInset: number;
}) {
  // Track the video's bottom-left, but never drop behind the program-mode ticker
  // (52px floor); compact left-column tiles keep their small 4px floor.
  const floor = compact ? 4 : 52;
  return (
    <div
      className="pointer-events-none absolute flex items-stretch"
      style={{ left: `${left}px`, bottom: `${Math.max(bottomInset, floor)}px` }}
    >
      <span
        className={cn('w-1 transition-colors', active ? 'bg-oxblood-live' : 'bg-oxblood-live/60')}
      />
      <span
        className={cn(
          'truncate font-serif font-semibold leading-none transition-colors',
          compact ? 'max-w-full px-2 py-1 text-[13px]' : 'whitespace-nowrap px-4 py-2 text-[21px]',
          active ? 'bg-oxblood text-white' : 'bg-oxblood/60 text-white/80',
        )}
      >
        {name}
      </span>
    </div>
  );
}

function OverlayLayer({
  roomStatus,
  title,
  tickerLines,
  logoUrl,
}: {
  roomStatus: string;
  title: string;
  tickerLines: string[];
  logoUrl?: string | null;
}) {
  return (
    <div className="pointer-events-none absolute inset-0">
      {/* Frame: top accent bar + thin inset border. */}
      <div className="absolute inset-x-0 top-0 h-1 bg-oxblood-live" />
      <div className="absolute inset-0 ring-1 ring-inset ring-oxblood/60" />

      {/* Logo bug (top-right): the show's uploaded logo, else the brand wordmark. */}
      {logoUrl ? (
        <img
          src={logoUrl}
          alt=""
          className="absolute right-3 top-3 h-7 w-auto max-w-[10rem] object-contain"
        />
      ) : (
        <Logo className="absolute right-3 top-3 h-7 w-auto max-w-[10rem] object-contain" />
      )}

      {/* LIVE bug (top-left). */}
      {roomStatus === 'live' && (
        <span className="absolute left-3 top-3 rounded-sm bg-oxblood-live px-2 py-0.5 font-sans text-xs font-semibold uppercase tracking-[0.12em] text-white">
          ● Live
        </span>
      )}

      <FeaturedSuperchat />
      <Ticker label={title} lines={tickerLines} />
    </div>
  );
}

// Superchat Spec 01 — the on-air read-aloud graphic. A lower-third card above
// the ticker, centered clear of the bottom-left nameplates, driven by the shared
// featured-superchat state so it shows for everyone on the stage (StudioStage,
// ViewerStage, and egress all render BroadcastView). Enters/exits with a subtle
// transition; respects prefers-reduced-motion. Text is render-clamped as
// defense-in-depth (the seam already bounds it).
const FEATURED_MAX_CHARS = 200;

function FeaturedSuperchat() {
  const { featured } = useFeaturedSuperchatContext();
  const reduced = usePrefersReducedMotion();
  // Keep the card mounted through its exit transition.
  const [rendered, setRendered] = useState(featured);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (featured) {
      setRendered(featured);
      const raf = requestAnimationFrame(() => setVisible(true));
      return () => cancelAnimationFrame(raf);
    }
    setVisible(false);
    const t = setTimeout(() => setRendered(null), 250);
    return () => clearTimeout(t);
  }, [featured]);

  if (!rendered) return null;

  return (
    <div className="pointer-events-none absolute inset-x-0 bottom-14 flex justify-center px-6">
      <div
        className={cn(
          'max-w-2xl overflow-hidden rounded-md bg-video-bg/90 shadow-lg ring-1 ring-superchat/40',
          'flex items-stretch',
          reduced ? 'transition-none' : 'transition-all duration-200 ease-out',
          visible ? 'translate-y-0 opacity-100' : 'translate-y-2 opacity-0',
        )}
      >
        <span className="w-1.5 flex-none bg-superchat" />
        <div className="min-w-0 px-4 py-2.5">
          <p className="font-sans leading-tight">
            <span className="text-lg font-bold text-superchat">{rendered.amount.display}</span>{' '}
            <span className="font-semibold text-video-text">{rendered.author.name}</span>
          </p>
          {rendered.text ? (
            <p className="mt-0.5 font-serif text-sm text-video-text-muted">
              {rendered.text.slice(0, FEATURED_MAX_CHARS)}
            </p>
          ) : null}
        </div>
      </div>
    </div>
  );
}

// Speed of the marquee in px/sec — duration is derived from content width so
// long and short tickers scroll at the same pace.
const TICKER_PX_PER_SEC = 60;

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(mq.matches);
    const onChange = () => setReduced(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function Ticker({ label, lines }: { label: string; lines: string[] }) {
  const reduced = usePrefersReducedMotion();
  const seqRef = useRef<HTMLDivElement>(null);
  const [duration, setDuration] = useState(0);
  const joined = `${label}␟${lines.join('␟')}`;
  useEffect(() => {
    const el = seqRef.current;
    if (el) setDuration(el.scrollWidth > 0 ? el.scrollWidth / TICKER_PX_PER_SEC : 0);
  }, [joined]);
  if (lines.length === 0) return null;

  const sep = <span className="mx-4 font-sans font-bold text-oxblood-live">{'//'}</span>;
  // Each line is followed by the show name, so the scroll reads
  // "line // Show // next line // Show // …" and loops seamlessly.
  const sequence = lines.map((line, i) => (
    <span key={i} className="flex items-center">
      <span className="font-sans text-sm font-semibold text-silver">{line}</span>
      {label && (
        <>
          {sep}
          <span className="whitespace-nowrap font-serif text-sm font-normal text-video-text-muted">
            {label}
          </span>
        </>
      )}
      {sep}
    </span>
  ));

  return (
    <div className="absolute inset-x-0 bottom-0 flex items-stretch bg-video-bg/90">
      <span className="flex flex-none items-center whitespace-nowrap bg-oxblood-live px-3 font-serif text-sm font-semibold text-white">
        {label || 'ArcheTelos'}
      </span>
      <div className="flex flex-1 items-center overflow-hidden py-2">
        {reduced ? (
          <div ref={seqRef} className="flex flex-none items-center">
            {sequence}
          </div>
        ) : (
          <div
            className="flex items-center whitespace-nowrap"
            style={duration ? { animation: `at-marquee ${duration}s linear infinite` } : undefined}
          >
            <div ref={seqRef} className="flex flex-none items-center">
              {sequence}
            </div>
            <div aria-hidden className="flex flex-none items-center">
              {sequence}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
